export interface SignUpConsentCheckboxConfig {
  enabled?: unknown;
  required?: unknown;
  /** When true, checking the box unsubscribes (opt-out). Marketing only. */
  optOut?: unknown;
  /** HTML label; supports `{{companyName}}`, `{{termsUrl}}`, `{{privacyUrl}}`. */
  labelHtml?: string;
  requiredMessage?: string;
}

export interface SignUpConsentCheckboxesConfig {
  termsOfService?: SignUpConsentCheckboxConfig;
  marketingEmails?: SignUpConsentCheckboxConfig;
}

export interface NormalizedSignUpConsentCheckboxConfig {
  enabled: boolean;
  required: boolean;
  optOut: boolean;
  labelHtml?: string;
  requiredMessage?: string;
}

export interface NormalizedSignUpConsentCheckboxesConfig {
  termsOfService: NormalizedSignUpConsentCheckboxConfig;
  marketingEmails: NormalizedSignUpConsentCheckboxConfig;
}

export interface SignUpConsentBrandingVars {
  companyName?: string;
  termsUrl?: string;
  privacyUrl?: string;
}

function parseConsentFlag(value: unknown, defaultValue: boolean): boolean {
  if (value === undefined || value === null) return defaultValue;
  if (value === false || value === 0) return false;
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (t === "false" || t === "0" || t === "no") return false;
    if (t === "true" || t === "1" || t === "yes") return true;
  }
  return defaultValue;
}

function normalizeOne(
  raw: SignUpConsentCheckboxConfig | undefined,
  defaults: { enabled: boolean; required: boolean }
): NormalizedSignUpConsentCheckboxConfig {
  return {
    enabled: parseConsentFlag(raw?.enabled, defaults.enabled),
    required: parseConsentFlag(raw?.required, defaults.required),
    optOut: parseConsentFlag(raw?.optOut, false),
    labelHtml: typeof raw?.labelHtml === "string" && raw.labelHtml.trim() ? raw.labelHtml : undefined,
    requiredMessage:
      typeof raw?.requiredMessage === "string" && raw.requiredMessage.trim() ? raw.requiredMessage : undefined,
  };
}

/** Map checkbox checked ↔ subscribeToMarketingEmails. Symmetric; use both ways. */
export function marketingConsentFromCheckbox(checked: boolean, optOut: boolean): boolean {
  return optOut ? !checked : Boolean(checked);
}

export function normalizeSignUpConsentCheckboxes(
  raw?: SignUpConsentCheckboxesConfig | null
): NormalizedSignUpConsentCheckboxesConfig {
  return {
    termsOfService: normalizeOne(raw?.termsOfService, { enabled: true, required: true }),
    marketingEmails: normalizeOne(raw?.marketingEmails, { enabled: true, required: false }),
  };
}

export function resolveSignUpConsentLabelHtml(
  labelHtml: string | undefined,
  vars: SignUpConsentBrandingVars
): string | null {
  if (!labelHtml?.trim()) return null;
  return labelHtml
    .replace(/\{\{companyName\}\}/g, vars.companyName ?? "")
    .replace(/\{\{termsUrl\}\}/g, vars.termsUrl ?? "#")
    .replace(/\{\{privacyUrl\}\}/g, vars.privacyUrl ?? "#");
}
